// Datos de ejemplo de la guía a fondo del equipo interno (D-141): la operación de un ciclo.
// Solo se usan en las rutas /admin/tutorial/... (ver demo.interceptor.ts).
import type { OrdenCola } from '../admin/cobro/cobro-admin.service';
import type { CicloCompras, GrupoVendedor, OrdenShein, Tablero, UnidadCompra } from '../admin/compras/compras.model';
import type { BusquedaEntrega, ResumenEntrega } from '../admin/entregas/entregas.model';
import type { Lote, OrdenPorRecibir, PedidoEnBodega } from '../admin/logistica/logistica.model';
import type { CuentaReceptora, OrdenPago } from '../vendedor/cobro/cobro.model';

const DIA = 86_400_000;
const hace = (dias: number, horas = 0) => new Date(Date.now() - dias * DIA - horas * 3_600_000).toISOString();
const en = (dias: number) => new Date(Date.now() + dias * DIA).toISOString();
const fecha = (dias: number) => hace(dias).slice(0, 10);

const CUENTA: CuentaReceptora = { id: 'demo-cr1', metodo: 'PAGO_MOVIL', banco_codigo: '0134', titular: 'Inversiones Vendia C.A.', documento: 'J-412345678', telefono_pm: '+584141112233', direccion_usdt: null, ultimos4: null, numero: null, activa: true };

// ── Cola de abonos ───────────────────────────────────────────────────────────
const base = (id: string, usd: string, ves: string, horas: number, extra: Partial<OrdenPago> = {}): OrdenPago => ({
  id,
  pedido_ciclo_id: `${id}-pc`,
  metodo: 'PAGO_MOVIL',
  monto_usd: usd,
  tasa_valor: '191.23000000',
  monto_ves: ves,
  factor_divisas: null,
  monto_usd_fisico: null,
  vence_en: en(0),
  estado: 'COMPROBANTE_CARGADO',
  comprobante_tipo: 'image/svg+xml',
  comprobante_cargado_en: hace(0, horas),
  referencia: '00451' + id.slice(-1),
  banco_origen: '0102',
  fecha_pago: hace(0, horas + 1),
  monto_ves_declarado: ves,
  monto_usd_fisico_declarado: null,
  posible_duplicado: false,
  credito_usd: null,
  validada_en: null,
  motivo_rechazo: null,
  detalle_rechazo: null,
  created_at: hace(0, horas + 2),
  cuentas: [CUENTA],
  ...extra,
});
const enCola = (o: OrdenPago, vendedor: [string, string], total: string, minimo: string, horas: number): OrdenCola => ({
  ...o,
  vendedor: { id: `v-${vendedor[1]}`, nombre: vendedor[0], codigo: vendedor[1] },
  pedido: { id: o.pedido_ciclo_id, estado: 'ABONO_EN_REVISION', total_a_pagar_usd: total, abono_minimo_usd: minimo, ciclo: 'Ciclo 15 de octubre' },
  credito_declarado_usd: o.monto_usd,
  horas_en_cola: horas,
  pendiente: { saldo: total, abonado: '0.0000', falta_minimo: minimo },
});
export const COLA_ABONOS: OrdenCola[] = [
  enCola(base('demo-ab1', '28.3750', '5426.15', 5), ['Lorena Pinto', 'T998J7'], '113.5000', '28.3750', 5),
  enCola(base('demo-ab2', '20.0000', '3824.60', 3, { posible_duplicado: true }), ['Sergio Alarcón', 'FYYAT3'], '80.0000', '20.0000', 3),
  enCola(base('demo-ab3', '45.0000', '0.00', 1, { metodo: 'EFECTIVO_USD', monto_ves: null, tasa_valor: null, comprobante_tipo: null, referencia: null, banco_origen: null, monto_ves_declarado: null, monto_usd_fisico: '45.00', monto_usd_fisico_declarado: '45.00', factor_divisas: '1.0000' }), ['Daniela Ortiz', 'K2M8QD'], '150.0000', '37.5000', 1),
];

// ── Compras del ciclo ────────────────────────────────────────────────────────
export const CICLOS_COMPRAS: CicloCompras[] = [
  { id: 'demo-cc1', nombre: 'Ciclo 30 de septiembre', estado: 'EN_COMPRA', cierre_en: hace(1), compras_iniciadas_en: hace(0, 6), compras_cerradas_en: null, pedidos_confirmados: 3, pedidos_por_confirmar: 0 },
];
const conc = (suma: string, total: string, cuadra: boolean) => ({ suma_unidades_cop: suma, diferencia_cop: (Number(suma) - Number(total)).toFixed(2), tolerancia_cop: '500.00', cuadra, ok: cuadra });
const ORDENES: OrdenShein[] = [
  { id: 'demo-os1', ciclo_id: 'demo-cc1', numero_orden: 'GSH18R633002T9P', fecha_compra: fecha(0), total_pagado_cop: '96400.00', envio_cop: '0.00', cupones: null, descuento_cupones_cop: '0.00', estado: 'PAGADA', rastreo: null, diferencia_aceptada: false, motivo_diferencia: null, notas: null, comprobante_tipo: null, tiene_comprobante: true, unidades: 4, version: 1, conciliacion: conc('96400.00', '96400.00', true) },
  { id: 'demo-os2', ciclo_id: 'demo-cc1', numero_orden: 'GSH18R633002X4K', fecha_compra: fecha(0), total_pagado_cop: '70000.00', envio_cop: '6000.00', cupones: null, descuento_cupones_cop: '0.00', estado: 'PAGADA', rastreo: null, diferencia_aceptada: false, motivo_diferencia: null, notas: null, comprobante_tipo: null, tiene_comprobante: false, unidades: 2, version: 1, conciliacion: conc('49800.00', '64000.00', false) },
];
const unidad = (id: string, titulo: string, n: number, talla: string, cotizado: string, estado: UnidadCompra['estado'], pagado: string | null, orden: string | null, v: [string, string], cliente: string): UnidadCompra => ({
  id,
  version: 1,
  estado,
  base_cop_cotizado: cotizado,
  precio_pagado_cop: pagado,
  precio_atipico_confirmado: false,
  orden_shein_id: orden,
  margen_compra_cop: pagado ? (Number(cotizado) - Number(pagado)).toFixed(2) : null,
  motivo_cancelacion: null,
  variante_id: `${id}-var`,
  talla,
  opciones: [{ nombre: 'Talla', valor: talla }],
  producto: { id: `${id}-p`, titulo, color_nombre: null, url_shein: null, imagen: { id: `demo-img-${n}`, url: `tutorial/prenda-${n}.svg` } },
  vendedor: { id: `v-${v[1]}`, codigo: v[1], nombre: v[0] },
  cliente: { pedido_cliente_id: `${id}-pcl`, nombre: cliente },
});
const LORENA: [string, string] = ['Lorena Pinto', 'T998J7'];
const SERGIO: [string, string] = ['Sergio Alarcón', 'FYYAT3'];
const grupo = (v: [string, string], clientes: [string, UnidadCompra[]][]): GrupoVendedor => {
  const todas = clientes.flatMap(([, u]) => u);
  return {
    vendedor: { id: `v-${v[1]}`, codigo: v[1], nombre: v[0] },
    unidades: todas.length,
    pendientes: todas.filter((u) => u.estado === 'CONFIRMADA').length,
    cotizado_cop: todas.reduce((s, u) => s + Number(u.base_cop_cotizado), 0).toFixed(2),
    clientes: clientes.map(([nombre, unidades], i) => ({ pedido_cliente_id: `${v[1]}-${i}`, nombre, unidades })),
  };
};
export const TABLERO: Tablero = {
  ciclo: { id: 'demo-cc1', nombre: 'Ciclo 30 de septiembre', estado: 'EN_COMPRA', cierre_en: hace(1), compras_iniciadas_en: hace(0, 6), compras_cerradas_en: null },
  vista: 'vendedor',
  resumen: { unidades: 8, compradas: 5, agotadas: 1, pendientes: 2, canceladas: 0, cotizado_cop: '214600.00', pagado_cop: '146200.00', total_ordenes_cop: '166400.00' },
  ordenes: ORDENES,
  faltantes: [
    { codigo: 'UNIDADES_PENDIENTES', mensaje: '2 prendas sin marcar como compradas o agotadas', cantidad: 2 },
    { codigo: 'ORDEN_SIN_CONCILIAR', mensaje: 'La orden GSH18R633002X4K no cuadra: faltan $ 14.200 por repartir entre sus prendas', orden_shein_id: 'demo-os2' },
  ],
  grupos: [
    grupo(LORENA, [
      ['María José Rangel', [unidad('demo-u1', 'Vestido midi de tirantes con abertura lateral', 1, 'M', '28500.00', 'COMPRADA', '24100.00', 'demo-os1', LORENA, 'María José Rangel'), unidad('demo-u2', 'Blusa de lino con botones y manga corta', 2, 'M', '22800.00', 'COMPRADA', '19900.00', 'demo-os1', LORENA, 'María José Rangel')]],
      ['Andreína Pérez', [unidad('demo-u3', 'Jeans de tiro alto y pierna recta', 3, '28', '31900.00', 'COMPRADA', '27600.00', 'demo-os1', LORENA, 'Andreína Pérez'), unidad('demo-u4', 'Jeans de tiro alto y pierna recta', 3, '28', '31900.00', 'AGOTADA', null, null, LORENA, 'Andreína Pérez')]],
    ]),
    grupo(SERGIO, [
      ['Carlos Mendoza', [unidad('demo-u5', 'Zapatillas deportivas con suela gruesa', 4, '42', '33600.00', 'COMPRADA', '24800.00', 'demo-os1', SERGIO, 'Carlos Mendoza'), unidad('demo-u6', 'Gorra de béisbol bordada', 5, 'Única', '14200.00', 'COMPRADA', '25000.00', 'demo-os2', SERGIO, 'Carlos Mendoza')]],
      ['Valentina Ruiz', [unidad('demo-u7', 'Conjunto deportivo de dos piezas', 6, 'S', '29400.00', 'CONFIRMADA', null, null, SERGIO, 'Valentina Ruiz'), unidad('demo-u8', 'Bolso tote de lona', 7, 'Única', '22300.00', 'CONFIRMADA', null, null, SERGIO, 'Valentina Ruiz')]],
    ]),
  ],
};
export const ORDENES_SHEIN = ORDENES.map((o) => ({ ...o, ciclo_nombre: 'Ciclo 30 de septiembre', ciclo_estado: 'EN_COMPRA' }));

// ── Bodega y lotes ───────────────────────────────────────────────────────────
export const BODEGA_ORDENES: OrdenPorRecibir[] = [
  { id: 'demo-or1', numero_orden: 'GSH18R5512A0QZ', fecha_compra: fecha(14), estado: 'ENVIADA', rastreo: 'SHN8823410CO', ciclo_nombre: 'Ciclo 15 de septiembre', unidades: 23, por_revisar: 23, con_problema: 0 },
  { id: 'demo-or2', numero_orden: 'GSH18R5512A7MB', fecha_compra: fecha(14), estado: 'RECIBIDA', rastreo: 'SHN8823411CO', ciclo_nombre: 'Ciclo 15 de septiembre', unidades: 17, por_revisar: 0, con_problema: 1 },
];
export const BODEGA_PEDIDOS: PedidoEnBodega[] = [
  { id: 'demo-pb1', punto_entrega_id: 'demo-pe1', punto_nombre: 'Caracas – Chacao', ciclo_nombre: 'Ciclo 15 de septiembre', vendedor_id: 'v-T998J7', codigo_vendedor: 'T998J7', vendedor_nombre: 'Lorena Pinto', bolsas: 3, unidades: 11 },
  { id: 'demo-pb2', punto_entrega_id: 'demo-pe2', punto_nombre: 'Valencia – Centro', ciclo_nombre: 'Ciclo 15 de septiembre', vendedor_id: 'v-FYYAT3', codigo_vendedor: 'FYYAT3', vendedor_nombre: 'Sergio Alarcón', bolsas: 2, unidades: 6 },
];
export const LOTES: Lote[] = [
  { id: 'demo-l1', codigo: 'L-0042', punto_entrega_id: 'demo-pe1', punto_nombre: 'Caracas – Chacao', punto_ciudad: 'Caracas', transportista: 'MRW', guia: '7700123', costo_cop: '85000.00', estado: 'DESPACHADO', fecha_despacho: fecha(2), llegada_estimada: en(2).slice(0, 10), despachado_en: hace(2), en_punto_en: null, notas: null, version: 2, pedidos: 4, bolsas: 9, bolsas_recibidas: 0, created_at: hace(3) },
  { id: 'demo-l2', codigo: 'L-0041', punto_entrega_id: 'demo-pe2', punto_nombre: 'Valencia – Centro', punto_ciudad: 'Valencia', transportista: 'Zoom', guia: '5512098', costo_cop: '62000.00', estado: 'EN_PUNTO', fecha_despacho: fecha(9), llegada_estimada: fecha(6), despachado_en: hace(9), en_punto_en: hace(6), notas: null, version: 3, pedidos: 3, bolsas: 5, bolsas_recibidas: 5, created_at: hace(10) },
];

// ── Entregas: una vendedora llega al punto a retirar ─────────────────────────
export const BUSQUEDA_ENTREGA: BusquedaEntrega = {
  vendedor: { id: 'v-T998J7', codigo: 'T998J7', nombre: 'Lorena Pinto', estado: 'ACTIVO' },
  pedidos: [{ id: 'demo-pe-pc', estado: 'EN_PUNTO', ciclo_nombre: 'Ciclo 30 de septiembre', punto_nombre: 'Caracas – Chacao', bolsas: 3, unidades: 7 }],
};
export const RESUMEN_ENTREGA: ResumenEntrega = {
  pedido: { id: 'demo-pe-pc', estado: 'EN_PUNTO', ciclo_nombre: 'Ciclo 30 de septiembre', total_a_pagar_usd: '81.5000' },
  vendedor: { id: 'v-T998J7', codigo: 'T998J7', nombre: 'Lorena Pinto', cedula: 'V-18.456.789', estado: 'ACTIVO' },
  financiero: {
    cargo_neto_usd: '78.0000',
    abonado_usd: '20.3750',
    saldo_usd: '57.6250',
    pct_minimo_para_retirar: '0.700000',
    minimo_usd: '54.6000',
    faltante_usd: '34.2250',
    deuda_otros_usd: '0.0000',
    deuda_tras_entrega_usd: '23.4000',
    limite_credito_usd: '150.00',
    dias_plazo_saldo: 15,
    a_pagar_usd: '34.2250',
    a_pagar_ves: '6544.85',
    tasa_bcv: '191.23000000',
    puede_entregar: false,
    motivo: 'MINIMO_PARA_RETIRAR',
  },
  bolsas: [
    { id: 'demo-b1', codigo: 'B-T998J7-1', token_qr: 'demo-qr-1', unidades: 3, entregada_en: null, cliente: 'María José Rangel' },
    { id: 'demo-b2', codigo: 'B-T998J7-2', token_qr: 'demo-qr-2', unidades: 2, entregada_en: null, cliente: 'Andreína Pérez' },
    { id: 'demo-b3', codigo: 'B-T998J7-3', token_qr: 'demo-qr-3', unidades: 2, entregada_en: null, cliente: 'Carlos Mendoza' },
  ],
  ordenes: [],
  saldo_a_favor_usd: '0.0000',
  cuentas: [CUENTA],
  metodos: ['PAGO_MOVIL', 'TRANSFERENCIA_VES', 'EFECTIVO_USD'],
  evidencia_obligatoria: false,
};
